import { useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useVictimsAndKillers } from "../../../hooks/useVictimsAndKillers";
import { useCustomMarkers } from "../../../hooks/useCustomMarkers";
import { useHighlight } from "../../../hooks/useHighlight";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 0;
`;

const TooltipDiv = styled.div`
  position: absolute;
  display: none;
  background: #393e46;
  color: #eeeeee;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 10;
  border: 1px solid #555e6e;
  line-height: 1.6;
  transform: translate(-50%, calc(-100% - 10px));
`;

const Canvas = styled.canvas`
  display: block;
  width: 100% !important;
`;

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #555e6e;
  font-size: 14px;
`;

const AXIS_W = 46;
const HEADER_H = 40;
const V_PAD = 20;
const DOT_R = 4;
const MIN_SPACING = 22;
const PX_PER_YEAR = 500;

export default function TimelineTab() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<
    { cx: number; cy: number; name: string; date: string }[]
  >([]);
  const { filters, victimsByFilter, serialKillerById } = useVictimsAndKillers();
  const { groups } = useCustomMarkers();
  const { highlightedIds, highlightColor } = useHighlight();

  // Always-fresh data ref so draw() never reads stale closure values
  const dataRef = useRef({
    filters,
    victimsByFilter,
    serialKillerById,
    groups,
    highlightedIds,
    highlightColor,
  });
  dataRef.current = {
    filters,
    victimsByFilter,
    serialKillerById,
    groups,
    highlightedIds,
    highlightColor,
  };

  const draw = useCallback(() => {
    const {
      filters: f,
      victimsByFilter: vbf,
      serialKillerById: skb,
      groups: g,
      highlightedIds: hIds,
      highlightColor: hColor,
    } = dataRef.current;

    const rows = f
      .map((filter) => ({
        filter,
        victims: (vbf[filter.id] ?? [])
          .filter((v) => v.dateOfCrime)
          .sort(
            (a, b) =>
              new Date(a.dateOfCrime!).getTime() -
              new Date(b.dateOfCrime!).getTime(),
          ),
        label: (() => {
          const killerName = skb[filter.killerId ?? ""]?.name;
          if (killerName) return killerName;
          const typeLabel =
            filter.type === "missing"
              ? "Missing Persons"
              : filter.type === "unsolved"
                ? "Unsolved"
                : "Crimes";
          const locationParts = [filter.country, filter.region].filter(Boolean);
          return locationParts.length > 0
            ? `${typeLabel} (${locationParts.join(", ")})`
            : typeLabel;
        })(),
      }))
      .filter((r) => r.victims.length > 0);

    // Custom marker groups that have at least one dated marker
    const markerCols = g
      .map((grp) => ({
        label: grp.name || "Markers",
        color: grp.color,
        items: grp.markers
          .filter((m) => m.date != null)
          .map((m) => ({
            id: m.id,
            name: m.name || "Marker",
            t: m.date!.toDate().getTime(),
          }))
          .sort((a, b) => a.t - b.t),
      }))
      .filter((c) => c.items.length > 0);

    const allDates = [
      ...rows.flatMap((r) =>
        r.victims.map((v) => new Date(v.dateOfCrime!).getTime()),
      ),
      ...markerCols.flatMap((c) => c.items.map((m) => m.t)),
    ];
    const minDate = allDates.length > 0 ? Math.min(...allDates) : 0;
    const maxDate = allDates.length > 0 ? Math.max(...allDates) : 1;
    const dateRange = maxDate - minDate || 1;
    const canvas = canvasRef.current;
    dotsRef.current = [];
    if (!canvas || (rows.length === 0 && markerCols.length === 0)) {
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const wrapperWidth = wrapperRef.current?.clientWidth ?? 500;
    const startYear = new Date(minDate).getFullYear();
    const endYear = new Date(maxDate).getFullYear();
    const yearSpan = Math.max(1, endYear - startYear + 1);
    const tlHeight = yearSpan * PX_PER_YEAR;
    const totalCols = rows.length + markerCols.length;
    const colW = Math.max(
      1,
      Math.floor((wrapperWidth - AXIS_W) / Math.max(1, totalCols)),
    );
    const canvasWidth = wrapperWidth;

    // tlTop/tlBottom are fixed — not dependent on canvasHeight
    const tlTop = HEADER_H + V_PAD;
    const tlBottom = tlTop + tlHeight;

    const dateToY = (t: number) =>
      tlTop + ((t - minDate) / dateRange) * (tlBottom - tlTop);

    // Dry-run: find the lowest nudged dot position (victims + markers)
    let maxY = tlBottom;
    rows.forEach((row) => {
      const placed: number[] = [];
      row.victims.forEach((v) => {
        let y = dateToY(new Date(v.dateOfCrime!).getTime());
        for (const py of placed) {
          if (Math.abs(y - py) < MIN_SPACING) y = py + MIN_SPACING;
        }
        placed.push(y);
        if (y > maxY) maxY = y;
      });
    });
    markerCols.forEach((col) => {
      const placed: number[] = [];
      col.items.forEach((m) => {
        let y = dateToY(m.t);
        for (const py of placed) {
          if (Math.abs(y - py) < MIN_SPACING) y = py + MIN_SPACING;
        }
        placed.push(y);
        if (y > maxY) maxY = y;
      });
    });

    // Canvas height fits all dots + label text below them (name ~11px + date ~9px + gap)
    const canvasHeight = maxY + 30;

    const truncate = (text: string, font: string, maxW: number) => {
      ctx.font = font;
      if (ctx.measureText(text).width <= maxW) return text;
      let lo = 0,
        hi = text.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (ctx.measureText(text.slice(0, mid) + "…").width <= maxW) lo = mid;
        else hi = mid - 1;
      }
      return lo === 0 ? "" : text.slice(0, lo) + "…";
    };

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const MONTH_ABBR = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Year gridlines + labels
    for (let yr = startYear; yr <= endYear + 1; yr++) {
      const t = new Date(`${yr}-01-01T00:00:00.000Z`).getTime();
      const y = dateToY(t);
      if (y < tlTop - 1 || y > tlBottom + 1) continue;
      ctx.strokeStyle = "#3a404a";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(AXIS_W, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
      ctx.fillStyle = "#8892a0";
      ctx.font = "bold 10px veterantypewriter, serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(String(yr), AXIS_W - 6, y);
    }

    // Month gridlines + labels (only when PX_PER_YEAR is large enough to be readable)
    const pxPerMonth = PX_PER_YEAR / 12;
    if (pxPerMonth >= 14) {
      for (let yr = startYear; yr <= endYear + 1; yr++) {
        for (let mo = 1; mo <= 11; mo++) {
          const t = new Date(
            `${yr}-${String(mo + 1).padStart(2, "0")}-01T00:00:00.000Z`,
          ).getTime();
          const y = dateToY(t);
          if (y < tlTop - 1 || y > tlBottom + 1) continue;
          ctx.strokeStyle = "#282d35";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(AXIS_W, y);
          ctx.lineTo(canvasWidth, y);
          ctx.stroke();
          ctx.fillStyle = "#555e6e";
          ctx.font = "10px veterantypewriter, serif";
          ctx.textAlign = "right";
          ctx.textBaseline = "middle";
          ctx.fillText(MONTH_ABBR[mo], AXIS_W - 6, y);
        }
      }
    }

    rows.forEach((row, i) => {
      const color = row.filter.color;
      const colX = AXIS_W + i * colW;
      const lineX = colX + DOT_R * 2 + 2;

      // Header label
      ctx.fillStyle = color;
      ctx.font = "bold 11px veterantypewriter, serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const headerLabel = truncate(
        row.label,
        "bold 11px veterantypewriter, serif",
        colW - 6,
      );
      ctx.fillText(headerLabel, colX + 2, HEADER_H / 2);

      // Vertical line
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(lineX, tlTop);
      ctx.lineTo(lineX, tlBottom);
      ctx.stroke();
      ctx.globalAlpha = 1;

      // Dots + names (collision-nudge to avoid stacking)
      const placedY: number[] = [];
      row.victims.forEach((v) => {
        const rawY = dateToY(new Date(v.dateOfCrime!).getTime());
        // push down if too close to previous dot
        let y = rawY;
        for (const py of placedY) {
          if (Math.abs(y - py) < MIN_SPACING) y = py + MIN_SPACING;
        }
        placedY.push(y);

        // Dot
        ctx.beginPath();
        ctx.arc(lineX, y, DOT_R, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        // Register dot for hover
        const fullDate = new Date(v.dateOfCrime!).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        dotsRef.current.push({
          cx: lineX,
          cy: y,
          name: v.name ?? "Unknown",
          date: fullDate,
        });

        // Name + date
        const fullDateStr = new Date(v.dateOfCrime!).toLocaleDateString(
          "en-GB",
          {
            day: "numeric",
            month: "short",
            year: "numeric",
          },
        );
        const shortDateStr = new Date(v.dateOfCrime!).toLocaleDateString(
          "en-GB",
          {
            month: "short",
            year: "numeric",
          },
        );
        const textX = lineX + DOT_R + 5;
        const maxTextW = colW - (lineX - (AXIS_W + i * colW)) - DOT_R - 8;
        const nameLabel = truncate(
          v.name ?? "Unknown",
          "11px veterantypewriter, serif",
          maxTextW,
        );
        ctx.font = "9px veterantypewriter, serif";
        const dateLabel =
          ctx.measureText(fullDateStr).width <= maxTextW
            ? fullDateStr
            : shortDateStr;
        if (v.id && hIds.has(v.id)) {
          ctx.font = "11px veterantypewriter, serif";
          const nameW = ctx.measureText(nameLabel).width;
          ctx.font = "9px veterantypewriter, serif";
          const dateW = ctx.measureText(dateLabel).width;
          const boxW = Math.max(nameW, dateW) + 6;
          ctx.globalAlpha = 0.25;
          ctx.fillStyle = hColor;
          ctx.fillRect(textX - 2, y - 9, boxW, 24);
          ctx.globalAlpha = 1;
        }
        ctx.fillStyle = "#eeeeee";
        ctx.font = "11px veterantypewriter, serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(nameLabel, textX, y - 1);
        ctx.font = "9px veterantypewriter, serif";
        ctx.fillStyle = "#8892a0";
        ctx.fillText(dateLabel, textX, y + 10);
      });
    });

    // Draw custom marker columns (diamond shape to distinguish from victim circles)
    markerCols.forEach((col, ci) => {
      const i = rows.length + ci;
      const color = col.color;
      const colX = AXIS_W + i * colW;
      const lineX = colX + DOT_R * 2 + 2;

      // Header label
      ctx.fillStyle = color;
      ctx.font = "bold 11px veterantypewriter, serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const headerLabel = truncate(
        col.label,
        "bold 11px veterantypewriter, serif",
        colW - 6,
      );
      ctx.fillText(headerLabel, colX + 2, HEADER_H / 2);

      // Vertical line
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(lineX, tlTop);
      ctx.lineTo(lineX, tlBottom);
      ctx.stroke();
      ctx.globalAlpha = 1;

      const placedY: number[] = [];
      col.items.forEach((m) => {
        let y = dateToY(m.t);
        for (const py of placedY) {
          if (Math.abs(y - py) < MIN_SPACING) y = py + MIN_SPACING;
        }
        placedY.push(y);

        // Diamond shape
        ctx.beginPath();
        ctx.moveTo(lineX, y - DOT_R - 1);
        ctx.lineTo(lineX + DOT_R + 1, y);
        ctx.lineTo(lineX, y + DOT_R + 1);
        ctx.lineTo(lineX - DOT_R - 1, y);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();

        const fullDate = new Date(m.t).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        dotsRef.current.push({
          cx: lineX,
          cy: y,
          name: m.name,
          date: fullDate,
        });

        const fullDateStr = new Date(m.t).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
        const shortDateStr = new Date(m.t).toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
        });
        const textX = lineX + DOT_R + 5;
        const maxTextW = colW - (lineX - colX) - DOT_R - 8;
        const nameLabel = truncate(
          m.name,
          "11px veterantypewriter, serif",
          maxTextW,
        );
        ctx.font = "9px veterantypewriter, serif";
        const dateLabel =
          ctx.measureText(fullDateStr).width <= maxTextW
            ? fullDateStr
            : shortDateStr;
        if (hIds.has(m.id)) {
          ctx.font = "11px veterantypewriter, serif";
          const nameW = ctx.measureText(nameLabel).width;
          ctx.font = "9px veterantypewriter, serif";
          const dateW = ctx.measureText(dateLabel).width;
          const boxW = Math.max(nameW, dateW) + 6;
          ctx.globalAlpha = 0.25;
          ctx.fillStyle = hColor;
          ctx.fillRect(textX - 2, y - 9, boxW, 24);
          ctx.globalAlpha = 1;
        }
        ctx.fillStyle = "#eeeeee";
        ctx.font = "11px veterantypewriter, serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(nameLabel, textX, y - 1);
        ctx.font = "9px veterantypewriter, serif";
        ctx.fillStyle = "#8892a0";
        ctx.fillText(dateLabel, textX, y + 10);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    draw();
  }, [
    filters,
    victimsByFilter,
    serialKillerById,
    groups,
    highlightedIds.size,
    highlightColor,
    draw,
  ]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => draw());
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [draw]);

  const hasData =
    filters.some((f) =>
      (victimsByFilter[f.id] ?? []).some((v) => v.dateOfCrime),
    ) || groups.some((g) => g.markers.some((m) => m.date != null));

  if (!hasData) {
    return <Empty>Add crimes with victims to see the timeline</Empty>;
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;
    const hit = dotsRef.current.find(
      (d) => Math.hypot(d.cx - mx, d.cy - my) <= DOT_R + 6,
    );
    const el = tooltipRef.current;
    if (!el) return;
    if (hit) {
      el.style.display = "block";
      el.style.left = `${e.clientX - rect.left}px`;
      el.style.top = `${hit.cy / scaleY}px`;
      el.innerHTML = `<div>${hit.name}</div><div style="color:#8892a0;font-size:11px">${hit.date}</div>`;
    } else {
      el.style.display = "none";
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          if (tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
      />
      <TooltipDiv ref={tooltipRef} />
    </Wrapper>
  );
}
